/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The comparer's shortlist: up to three offers a visitor ticks to see side by
 * side on /vergelijk (docs/plan-vergelijker-resultaten-2026-09-30.md §3.6).
 *
 * Keyed by provider SLUG — the key /vergelijk?ids= and groupProviders use —
 * so a provider with two visible rows (looptijd "Alle") shows both boxes
 * ticked: the grid compares providers, and that is honest.
 *
 * Remembered in localStorage for 30 days (Jasper 2026-10-01: a remembered
 * shortlist), and read ONLY in an effect after mount: the baked HTML has no
 * storage, so the first client render must be the empty list it was baked
 * with or hydration fails. Storage is wrapped in try/catch — a private window
 * or a blocked store leaves the feature working for the tab. The entry is
 * pruned against the rows actually loaded, so a slug that left the comparison
 * never lingers in the tray.
 *
 * Events reuse the aanbiedersoverzicht's vocabulary (`compare_select` /
 * `compare_remove`, ANALYTICS-EVENTS.md) under the comparer's surface. Never
 * inside a state updater (StrictMode double-invokes those).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { SURFACES, trackEvent } from '../lib/gtag';
import type { EREProvider } from '../types';

export const SHORTLIST_MAX = 3;
export const SHORTLIST_STORAGE_KEY = 'ere:compare:shortlist';
const DAY_MS = 24 * 60 * 60 * 1000;
const SHORTLIST_TTL_MS = 30 * DAY_MS;
/**
 * A stamp further ahead than this is a clock that was wrong when it was
 * written, not a recent tick: it could never expire (the rule of
 * src/lib/compareMemory.ts).
 */
const FUTURE_TOLERANCE_MS = DAY_MS;

interface StoredShortlist {
  v: 1;
  at: number;
  slugs: string[];
}

/**
 * The stored slugs in tick order, each once, at most SHORTLIST_MAX — or []
 * when absent, corrupt, expired, stamped more than a day ahead, or unreadable.
 * A repeated slug is dropped before the cap, so a damaged entry cannot push a
 * third offer out with a duplicate of the first.
 */
export function readStoredShortlist(now = Date.now()): string[] {
  try {
    const raw = localStorage.getItem(SHORTLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<StoredShortlist>;
    if (parsed.v !== 1 || typeof parsed.at !== 'number' || !Array.isArray(parsed.slugs)) return [];
    if (!Number.isFinite(parsed.at)) return [];
    const age = now - parsed.at;
    if (age > SHORTLIST_TTL_MS || age < -FUTURE_TOLERANCE_MS) return [];
    const slugs = parsed.slugs.filter((s): s is string => typeof s === 'string');
    return [...new Set(slugs)].slice(0, SHORTLIST_MAX);
  } catch {
    return [];
  }
}

function writeStoredShortlist(slugs: string[], now = Date.now()): void {
  try {
    if (slugs.length === 0) {
      localStorage.removeItem(SHORTLIST_STORAGE_KEY);
      return;
    }
    const entry: StoredShortlist = { v: 1, at: now, slugs };
    localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable: the tab keeps its in-memory list */
  }
}

export interface Shortlist {
  /** Selected provider slugs, in tick order (≤ SHORTLIST_MAX). */
  slugs: string[];
  isSelected: (slug: string | undefined) => boolean;
  /** False once the list is full; a selected row can always be unticked. */
  canAdd: boolean;
  /** Tick or untick a row; `position` = its 1-based rank for the event. */
  toggle: (provider: EREProvider, position: number) => void;
  remove: (slug: string) => void;
  clear: () => void;
}

export function useShortlist(providers: EREProvider[], language: string): Shortlist {
  const [slugs, setSlugs] = useState<string[]>([]);
  const hydrated = useRef(false);

  // Restore after mount, once the rows are known: prune what is no longer in
  // the comparison. Never in the initial state (see the module comment).
  useEffect(() => {
    if (hydrated.current || providers.length === 0) return;
    hydrated.current = true;
    const known = new Set(providers.map((p) => p.slug).filter(Boolean));
    const stored = readStoredShortlist().filter((s) => known.has(s));
    if (stored.length > 0) setSlugs(stored);
  }, [providers]);

  // Persist every change the visitor makes (not the restore itself: writing
  // the same list back only refreshes `at`, which would make the 30 days roll).
  const persist = useCallback((next: string[]) => {
    setSlugs(next);
    writeStoredShortlist(next);
  }, []);

  const isSelected = useCallback(
    (slug: string | undefined) => !!slug && slugs.includes(slug),
    [slugs]
  );

  const toggle = useCallback(
    (provider: EREProvider, position: number) => {
      const slug = provider.slug;
      if (!slug) return;
      const selected = slugs.includes(slug);
      if (!selected && slugs.length >= SHORTLIST_MAX) return;
      const next = selected ? slugs.filter((s) => s !== slug) : [...slugs, slug];
      // Tracked here, outside any updater, so StrictMode cannot double-fire it.
      trackEvent(selected ? 'compare_remove' : 'compare_select', {
        provider_slug: slug,
        position,
        selected_count: next.length,
        surface: SURFACES.comparisonTable,
        language,
      });
      persist(next);
    },
    [slugs, language, persist]
  );

  const remove = useCallback(
    (slug: string) => {
      if (!slugs.includes(slug)) return;
      const next = slugs.filter((s) => s !== slug);
      trackEvent('compare_remove', {
        provider_slug: slug,
        selected_count: next.length,
        surface: SURFACES.comparisonTable,
        language,
      });
      persist(next);
    },
    [slugs, language, persist]
  );

  const clear = useCallback(() => {
    if (slugs.length === 0) return;
    trackEvent('compare_clear', {
      selected_count: slugs.length,
      surface: SURFACES.comparisonTable,
      language,
    });
    persist([]);
  }, [slugs, language, persist]);

  return { slugs, isSelected, canAdd: slugs.length < SHORTLIST_MAX, toggle, remove, clear };
}
